import type { ReactNode } from 'react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { BrandLogo, useBrand } from '@papan/brand/react'

interface AuthLayoutProps {
  children: ReactNode
  footer?: ReactNode
  onDevPasswordClick?: () => void
  /**
   * A wider column with no card and no brand block: the page draws its own grouped cards and header (the MCP consent
   * sheet, which shows the app and the product side by side). Theme and scrolling are the same.
   */
  sheet?: boolean
}

export function AuthLayout({ children, footer, onDevPasswordClick, sheet = false }: AuthLayoutProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // This is the single chrome behind all five auth pages, and it renders for
  // ANONYMOUS visitors — the population brand resolution exists to serve.
  const { brand, productName } = useBrand()
  // The wide lockup, in the variant this card's surface can actually show.
  //
  // The two slots are not a preference pair: `logo` is drawn in dark type and
  // `darkLogo` in white, and each is close to invisible on the other's
  // background. The light theme paints a white card, the dark and dim themes a
  // near-black one, so the theme picks the file. A brand that ships only one of
  // them still gets it on its own surface and falls through to the mark below
  // on the other, rather than showing a logo nobody can read.
  const lockup = settings.theme.workspace === 'light' ? brand.assets.logo : brand.assets.darkLogo

  return (
    // h-dvh + overflow-y-auto so the card scrolls WITHIN the page on short
    // viewports (keyboard up) — `body` is overflow-hidden app-wide (no page
    // rubber-band). `min-h-full` on the inner wrapper keeps the card centered
    // when it fits and lets it grow (not clip) when it doesn't.
    <div className={`${themeClass} h-dvh overflow-y-auto overscroll-contain themed-bg-primary`}>
      <div className="min-h-full flex items-center justify-center p-4">
      <div className={sheet ? 'w-full max-w-md py-8' : 'w-full max-w-sm p-8 themed-bg-secondary rounded-lg border themed-border'}>
        {/* A lockup REPLACES the heading — it already carries the product name,
            and rendering both prints it twice. Without one, the mark sits above
            the name instead: the mark is theme-neutral by construction, and
            BrandLogo falls back to the prism glyph for a brand that ships no
            artwork — never to the vendor's own logo. */}
        {/* `auth-brand` wraps BOTH branches so a spec can assert "the card
            opens with artwork" without knowing which one a deployment takes —
            that is the invariant (it used to be a bare text heading), and the
            branch chosen is the brand's business. `data-variant` names it for
            a spec that does care. */}
        {sheet ? null : lockup ? (
          <div data-testid="auth-brand" data-variant="lockup">
            <img
              src={lockup}
              alt={productName}
              className="mx-auto mb-6 w-full max-w-[240px] h-auto"
            />
          </div>
        ) : (
          <div
            className="mb-6 flex flex-col items-center gap-3"
            data-testid="auth-brand"
            data-variant="mark"
          >
            <BrandLogo decorative className="h-14 w-14 object-contain" />
            <h1 className="text-2xl font-bold themed-text text-center">{productName}</h1>
          </div>
        )}
        {children}
        {footer && <div className="mt-6 text-sm text-center themed-text-muted">{footer}</div>}
        {import.meta.env.DEV && (
          <p className="mt-4 text-[11px] themed-text-muted text-center opacity-60">
            Dev: kok@papan.local &middot;{' '}
            <span
              onClick={onDevPasswordClick}
              className={onDevPasswordClick ? 'cursor-pointer hover:text-blue-400 transition-colors' : ''}
            >
              PapanDev2026!
            </span>
          </p>
        )}
      </div>
      </div>
    </div>
  )
}

export function OrDivider({ text = 'or' }: { text?: string }) {
  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 border-t themed-border" />
      <span className="text-xs themed-text-muted">{text}</span>
      <div className="flex-1 border-t themed-border" />
    </div>
  )
}
